import { useCallback, useEffect } from 'react';
import { type EditorView } from '@tiptap/pm/view';

import { logger } from '@zep/core-logger';

import { prepareTiptapHTML } from '@/shared/features/rich-text';
import { extractBareText } from '@/shared/utils/extractBareText';

import {
  goToNextFocusable,
  goToPreviousFocusable,
} from '../../focus/focusChain';
import {
  isNextFocusKeyboardEvent,
  isPreviousFocusKeyboardEvent,
} from '../../focus/focusUtils';
import { useQuizList } from '../../useQuizList';
import { useBuildTextEditor } from '../hook/useBuildTextEditor';
import { useQuizFormField } from '../hook/useQuizFormField';
import { useQuizControl } from '../QuizControlProvider';

import { useAiGenerateMultipleStatusValue } from './useAiGenerateMultipleStatus';

export const useMultipleOptionTextEditor = ({
  choiceIndex,
  isLastOption,
}: {
  choiceIndex: number;
  isLastOption: boolean;
}) => {
  const { generatingIndexes, isGeneratingBulk } =
    useAiGenerateMultipleStatusValue();

  const { quizList, handleCreate } = useQuizList();
  const { index: quizIndex } = useQuizControl();

  const shouldBlur =
    generatingIndexes.includes(choiceIndex) || isGeneratingBulk;

  const [text, updateText] = useQuizFormField({
    field: `multipleData.choiceList.${choiceIndex}.text`,
  });
  const [richText, updateRichText] = useQuizFormField({
    field: `multipleData.choiceList.${choiceIndex}.richText`,
  });

  const { editor } = useBuildTextEditor({
    content: richText || (text ? prepareTiptapHTML(text) : ''),
    onUpdate: (content: string) => {
      updateText(extractBareText(content));
      updateRichText(content);
    },
  });

  const handleKeyDown = useCallback(
    (_: EditorView, event: KeyboardEvent) => {
      //새로운 퀴즈를 만들지를 먼저 결정
      if (
        quizList.length === quizIndex + 1 &&
        isLastOption &&
        (event.key === 'Enter' || event.key === 'Tab')
      ) {
        event.preventDefault();
        handleCreate('multiple');
        logger.track('add_quizItem', {
          type: 'multiple',
          position: 'shortcut_key',
        });
        return true;
      }

      if (isNextFocusKeyboardEvent(event)) {
        event.preventDefault();
        goToNextFocusable({
          current: `quizList.${quizIndex}.multipleData.choiceList.${choiceIndex}.text`,
        });
        return true;
      }
      if (isPreviousFocusKeyboardEvent(event)) {
        event.preventDefault();
        goToPreviousFocusable({
          current: `quizList.${quizIndex}.multipleData.choiceList.${choiceIndex}.text`,
        });
        return true;
      }
    },
    [choiceIndex, isLastOption, quizIndex, quizList.length, handleCreate],
  );

  editor?.setOptions({
    editorProps: {
      handleKeyDown,
    },
  });

  useEffect(() => {
    if (shouldBlur) {
      editor?.commands.blur();
    }
  }, [shouldBlur, editor]);

  return { editor };
};
